<template>
  <div class="z-0 size-full bg-gpx_gray-700" id="map" ref="mapContainer" />
</template>

<script setup lang="ts">
import * as _ from 'lodash';
import {
  toRaw,
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useStore } from 'vuex';
import L from 'leaflet';
import { antPath } from 'leaflet-ant-path';
import * as geojsonUtils from './utils/geo-json';
import * as antPathUtils from './utils/ant-path';
import * as circleUtils from './utils/circle';
import * as layerUtils from '../../../utils/map/layer';
import Geohash from '@/utils/geo-hash';
import { reverseCoords } from '@/utils/coords';
import { DEFAULT_ICONS } from '@/utils/map/new-markers';
import { mapCenter } from '@/config/constants';
import { useMapResize } from '@/utils/map/resize';
import { useAiActions } from '@/utils/map/aiActions.ts';
import {
  addOverlayIfEnabled,
  createOverlayPane,
  renderMapOverlays,
} from '@/utils/map/overlay';
import { getAccountMapOverlays } from '@gpx-spa/queries';

// Props
const props = defineProps({
  stopsHistory: {
    type: Object,
    default: null,
  },
  positionsHistory: {
    type: Object,
    default: null,
  },
  // The tracker's latest known position, rendered as a standalone marker so it
  // shows even when no period points fall in the selected date range.
  currentPosition: {
    type: Object,
    default: null,
  },
  // Use pared-down point popups (address + last report) instead of the full
  // speed/fix/battery readout. Used by the public tracker page.
  simplePopups: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const accountId = computed(() => store.getters['auth/accountId']);

// Reactive state (replaces data())
const dbClickTimer = ref(null);
const center = ref(mapCenter);
const map = ref(null);
const mainLayers = ref([]);
const overlays = ref({});
const layerControl = ref(null);
const tracksPath = ref(null);
const currentMarker = ref(null);
const trackPoints = ref(L.layerGroup([]));
const tracksGroup = ref(L.layerGroup([]));
const stopsGroup = ref(L.layerGroup([]));
const cleanupResizeObserver = ref(null);
const unsubscribeFromAiActions = ref(null);
const mapContainer = ref(null);

const mapOverlayGroup = L.layerGroup([]);
const { data: mapOverlaysData } = getAccountMapOverlays(accountId);

// Watchers
watch(
  [() => map.value, () => mapOverlaysData.value],
  ([mapInstance, overlaysData]) => {
    if (!mapInstance) return;
    renderMapOverlays(toRaw(mapInstance), overlaysData || [], mapOverlayGroup);
  },
  { immediate: true },
);

watch(
  () => props.positionsHistory,
  (n, o) => {
    if (_.isUndefined(n)) return;
    if (_.isEqual(n, o)) return;
    setTracksData();
  },
);

watch(
  () => props.stopsHistory,
  (n, o) => {
    if (_.isUndefined(n)) return;
    if (_.isEqual(n, o)) return;
    setStopsData();
  },
);

watch(
  () => props.currentPosition,
  (n, o) => {
    if (_.isEqual(n, o)) return;
    setCurrentPositionMarker();
  },
);

// Lifecycle hooks
onMounted(() => {
  setupLeafletMap();
  setCurrentPositionMarker();
  setTracksData();
  setStopsData();

  nextTick(() => {
    cleanupResizeObserver.value = useMapResize(map, mapContainer, { delay: 0 });
    unsubscribeFromAiActions.value = useAiActions(map);
  });
});

onBeforeUnmount(() => {
  if (cleanupResizeObserver.value) {
    cleanupResizeObserver.value();
    cleanupResizeObserver.value = null;
  }

  if (unsubscribeFromAiActions.value) {
    unsubscribeFromAiActions.value();
    unsubscribeFromAiActions.value = null;
  }
});

// Methods (rewritten as functions)
function setupLeafletMap() {
  const layers = layerUtils.getLayers();
  const { id } = layerUtils.useLocalStorageLayer();
  mainLayers.value = layers;

  const overlaysObj = {
    'Show tracks': toRaw(tracksGroup.value),
    'Show stops': toRaw(stopsGroup.value),
    'Show overlays': mapOverlayGroup,
  };
  overlays.value = overlaysObj;

  map.value = L.map('map', {
    layers: [layers[id]],
    center: toRaw(center.value),
    zoomControl: false,
    zoom: 5,
    minZoom: 1,
  });

  L.control.zoom({ position: 'topright' }).addTo(toRaw(map.value));

  layerControl.value = L.control
    .layers(toRaw(mainLayers.value), toRaw(overlays.value), {
      position: 'topleft',
    })
    .addTo(toRaw(map.value));

  createOverlayPane(toRaw(map.value));

  addOverlayIfEnabled(toRaw(map.value), mapOverlayGroup, 'mapOverlays');

  setupTracksOverlay();
  setupStopsOverlay();

  listenOverLayChange();
  listenLayerChange();
}

function setupTracksOverlay() {
  const { is } = layerUtils.useLocalStorageOverlay('tracks');
  if (is) {
    toRaw(tracksGroup.value).addTo(toRaw(map.value));
  }
}

function setupStopsOverlay() {
  const { is } = layerUtils.useLocalStorageOverlay('stops');
  if (is) {
    toRaw(stopsGroup.value).addTo(toRaw(map.value));
  }
}

function setTracksView(is) {
  if (is) {
    toRaw(map.value).fitBounds(toRaw(tracksPath.value).getBounds());
  }
}

function currentPositionLatLng() {
  const location = props.currentPosition?.location;
  if (!location || location.length < 2) return null;
  return reverseCoords(location);
}

function setCurrentPositionMarker() {
  const mapLocal = toRaw(map.value);
  if (!mapLocal) return;

  if (currentMarker.value) {
    mapLocal.removeLayer(toRaw(currentMarker.value));
    currentMarker.value = null;
  }

  const latlng = currentPositionLatLng();
  if (!latlng) return;

  const marker = L.marker(latlng, { icon: DEFAULT_ICONS.blue });
  marker.bindPopup(geojsonUtils.getSimplePositionPopupContent(props.currentPosition), {
    closeButton: false,
    minWidth: 200,
  });
  marker.addTo(mapLocal);
  currentMarker.value = marker;

  fitContentView();
}

// Frame both the track path and the current-position marker. Falls back to
// centering on the marker when there is no history to fit.
function fitContentView() {
  const mapLocal = toRaw(map.value);
  if (!mapLocal) return;

  const currentLatLng = currentPositionLatLng();
  const hasTracks = tracksPath.value && !_.isEmpty(props.positionsHistory);

  if (hasTracks) {
    const bounds = toRaw(tracksPath.value).getBounds();
    if (currentLatLng) bounds.extend(currentLatLng);
    mapLocal.fitBounds(bounds);
  } else if (currentLatLng) {
    mapLocal.setView(currentLatLng, 13);
  }
}

function setPositionFixes(positionsHistory) {
  const currentFixes = {};
  const fixes = L.featureGroup([]);

  positionsHistory.forEach((position) => {
    const [lat, lng] = reverseCoords(position.location);
    const hash = Geohash.encode(lat, lng, 9);
    if (!currentFixes[hash]) {
      if (position.fix === 'cell') {
        currentFixes[hash] = true;
        const fix = circleUtils.createCellCircle(position);
        fixes.addLayer(fix);
      }
      if (position.fix === 'wifi') {
        currentFixes[hash] = true;
        const fix = circleUtils.createWifiCircle(position);
        fixes.addLayer(fix);
      }
    }
  });
  fixes.addTo(toRaw(tracksGroup.value));
}

function setTracksData() {
  const mapLocal = toRaw(map.value);
  const mainLayersLocal = toRaw(mainLayers.value);

  if (_.isEmpty(props.positionsHistory)) {
    toRaw(tracksGroup.value).clearLayers();
    toRaw(trackPoints.value).clearLayers();
    tracksPath.value = null;
    fitContentView();
    return;
  }

  toRaw(tracksGroup.value).clearLayers();
  const coordsForAntPath = props.positionsHistory.map((h) =>
    reverseCoords(h.location),
  );
  tracksPath.value = antPath(coordsForAntPath, {
    ...antPathUtils.getStyle(mapLocal, mainLayersLocal),
    delay: 4e3,
    weight: 4,
    hardwareAccelerated: true,
  });

  setPositionFixes(props.positionsHistory);
  const featureCollection = geojsonUtils.convertToPositionPointFC({
    path: props.positionsHistory,
    popupBuilder: props.simplePopups
      ? geojsonUtils.getSimplePositionPopupContent
      : undefined,
  });
  trackPoints.value = L.geoJSON(featureCollection, {
    onEachFeature: geojsonUtils.onEachPositionPointFeature,
    pointToLayer: (point, latlng) =>
      geojsonUtils.createPosition(point, latlng, {
        map: mapLocal,
        mainLayers: mainLayersLocal,
      }),
  });
  toRaw(trackPoints.value).addTo(toRaw(tracksGroup.value));
  toRaw(tracksPath.value).addTo(toRaw(tracksGroup.value));
  fitContentView();
}

function setStopsData() {
  if (_.isNil(props.stopsHistory)) return;
  if (_.isEmpty(props.stopsHistory)) {
    toRaw(stopsGroup.value).clearLayers();
    return;
  }
  toRaw(stopsGroup.value).clearLayers();

  const featureCollection = geojsonUtils.convertToStopCircleFC(
    props.stopsHistory,
  );
  const stopPoints = L.geoJSON(featureCollection, {
    onEachFeature: geojsonUtils.onEachStopCircleFeature,
    pointToLayer: (point, latlng) => geojsonUtils.createStop(point, latlng),
  });
  toRaw(stopPoints).addTo(toRaw(stopsGroup.value));
}

function listenLayerChange() {
  const { set } = layerUtils.useLocalStorageLayer();
  toRaw(map.value).on('baselayerchange', (e) => {
    set(e.name);

    if (tracksPath.value) {
      const style = antPathUtils.getStyle(toRaw(map.value), mainLayers.value);
      tracksPath.value.setStyle(style);
    }
    if (trackPoints.value) {
      toRaw(trackPoints.value).eachLayer(function (layer) {
        const { name, speed } = layer.feature.properties;
        if (name === 'Position') {
          layer.setStyle(geojsonUtils.getPositionStyle(e.name, speed));
        }
      });
    }
  });
}

function listenOverLayChange() {
  const { set: setStopsValue } = layerUtils.useLocalStorageOverlay('stops');
  const { set: setTracksValue } = layerUtils.useLocalStorageOverlay('tracks');
  const { set: setMapOverlaysValue } =
    layerUtils.useLocalStorageOverlay('mapOverlays');

  toRaw(map.value).on('overlayadd', (e) => {
    if (e.name === 'Show tracks') {
      setTracksValue(true);
      if (tracksPath.value) {
        setTracksView(true);
      }
    }
    if (e.name === 'Show stops') {
      setStopsValue(true);
    }
    if (e.name === 'Show overlays') {
      setMapOverlaysValue(true);
    }
  });
  toRaw(map.value).on('overlayremove', (e) => {
    if (e.name === 'Show tracks') {
      setTracksValue(false);
    }
    if (e.name === 'Show stops') {
      setStopsValue(false);
    }
    if (e.name === 'Show overlays') {
      setMapOverlaysValue(false);
    }
  });
}
</script>

<style></style>
